import { EventEmitter, Injectable, Injector, OnDestroy } from "@angular/core";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { AppService } from "@app/app.service";
import { SONNER_DEFAULT_CONFIG } from "@app/config/sonner";
import { DeletionUndoService } from "@app/services/deletion/deletion-undo.service";
import { EditUndoService } from "@app/services/edit-undo/edit-undo.service";
import { MixedTimesSyncService } from "@app/services/mixed-times-sync.service";
import { dismissTimeEntryUndoToast, TIME_ENTRY_UNDO_TOAST_ID } from "@app/services/time-entry-undo-toast.util";
import { UntilDestroy } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import { FeedQuery, Logger, MyTimesQuery, MyTimesService, Time } from "@timeghost/web-api";
import { MyTimesStore } from "@timeghost/web-api/lib/stores/myTimes/myTimes.store";
import { merge } from "lodash-es";
import { ExternalToast, toast } from "ngx-sonner";
import { firstValueFrom, of } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import type { CreateUndoEvent, CreateUndoOffer, SingleCreateUndoOffer } from "./create-undo.types";

export type { CreateUndoEvent, CreateUndoOffer, SingleCreateUndoOffer } from "./create-undo.types";

const log = new Logger("CreateUndoService");

/** Time window to undo creates */
const UNDO_TIMEOUT_MS = 12000;

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class CreateUndoService implements OnDestroy {
	private pending = new Map<string, { entries: Time[] }>();
	private reverting = new Set<string>();

	/** Emitted after created entries were removed via undo. */
	readonly undone = new EventEmitter<CreateUndoEvent>(true);

	private get store(): MyTimesStore {
		return this.myTimesQuery.__store__ as MyTimesStore;
	}

	constructor(
		private myTimesQuery: MyTimesQuery,
		private myTimesService: MyTimesService,
		private feedQuery: FeedQuery,
		private translateService: TranslateService,
		private injector: Injector,
		private mixedTimesSyncService: MixedTimesSyncService
	) {}

	/** Clears tracked undo state and dismisses the shared undo toast. */
	dismissPendingUndoToasts(): void {
		dismissTimeEntryUndoToast();
		this.pending.clear();
	}

	/** Dismisses all time-entry undo toasts (create, edit, and delete). */
	dismissAllPendingUndoToasts(): void {
		this.dismissPendingUndoToasts();
		this.injector.get(EditUndoService).dismissPendingUndoToasts();
		this.injector.get(DeletionUndoService).dismissPendingUndoToasts();
	}

	/**
	 * Shows a success toast with undo for a single successfully created entry.
	 * Undo removes the entry again without offering a nested delete undo toast.
	 */
	offerSingleUndo(offer: SingleCreateUndoOffer): void {
		this.offerUndo({
			entries: [offer.entry],
			successDescriptionKey: offer.successDescriptionKey ?? "success.created",
		});
	}

	/**
	 * Shows a success toast with undo for successfully created entries.
	 * Undo removes the entries again without offering a nested delete undo toast.
	 */
	offerUndo(offer: CreateUndoOffer): void {
		const entries = offer.entries.filter(Boolean);
		if (!entries.length) return;

		this.dismissAllPendingUndoToasts();

		const key = `createUndo-${Date.now()}`;
		const undoAction = {
			label: this.translateService.instant("utils.undo"),
			onClick: (): void => {
				if (!this.pending.has(key)) return;
				void this.revert(key, entries);
			},
		};

		const successDescription = offer.successDescriptionKey
			? this.translateService.instant(offer.successDescriptionKey)
			: entries.length === 1
				? this.translateService.instant("success.created")
				: this.translateService.instant("success.created-plural");

		toast.success(
			null,
			merge({}, { ...SONNER_DEFAULT_CONFIG }, {
				id: TIME_ENTRY_UNDO_TOAST_ID,
				duration: UNDO_TIMEOUT_MS,
				description: successDescription,
				action: undoAction,
				onDismiss: () => this.pending.delete(key),
				onAutoClose: () => this.pending.delete(key),
			} as ExternalToast)
		);

		this.pending.set(key, { entries });
	}

	private async revert(key: string, entries: Time[]): Promise<void> {
		if (this.reverting.has(key)) return;

		if (this.pending.has(key)) {
			dismissTimeEntryUndoToast();
			this.pending.delete(key);
		}

		this.reverting.add(key);
		const removedIds: string[] = [];

		try {
			for (const entry of entries) {
				const result = await firstValueFrom(
					this.myTimesService.delete(entry).pipe(
						catchError((err) => {
							pushError(err);
							return of(null);
						}),
						finalize(() => {
							if (entry.outlookCalenderReference) {
								this.feedQuery.__store__.upsert(entry.outlookCalenderReference, { booked: false });
							}
							this.store.remove(entry.id);
						})
					)
				);
				if (result !== null) {
					removedIds.push(entry.id);
					this.mixedTimesSyncService.closeCorrectionDialogIfResolved(entry);
					this.mixedTimesSyncService.handleProjectTimeDelete(entry);
				}
			}
			if (removedIds.length) {
				this.injector
					.get(AppService)
					.notifySuccess(removedIds.length === 1 ? "success.deleted" : "success.deleted-plural");
				this.undone.emit({ key, entryIds: removedIds });
			}
		} catch (err) {
			log.error("Create undo failed", err);
		} finally {
			this.reverting.delete(key);
		}
	}

	ngOnDestroy(): void {
		this.dismissPendingUndoToasts();
	}
}
